import React, { useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { IReject } from '@/stack-shared/interfaces/model/reject.interface';
import { formatDateHourMinuteSecond } from '@/stack-shared/utils/gen-utils';
import ReportRejectActionButton from './ReportRejectActionButton';
import { useRejectsByLocation } from '@/hooks/api/useRejects';
import { useTrks } from '@/hooks/api/useTrks';
import { useAtms } from '@/hooks/api/useAtms';
import { Edit } from '@mui/icons-material';

interface LocationRejectsTableProps {
  locationId: string | number;
  dateFilter?: Date;
}

const LocationRejectsTable: React.FC<LocationRejectsTableProps> = ({ 
  locationId, 
  dateFilter 
}) => {
  const { data: rejectsData } = useRejectsByLocation(Number(locationId), dateFilter);
  const { data: atmsData } = useAtms();
  const { data: trksData } = useTrks();
  
  const allRejects = useMemo(() => {
    if (!rejectsData) return [];
    return Array.isArray(rejectsData) ? rejectsData : Object.values(rejectsData);
  }, [rejectsData]);
  
  const atms = useMemo(() => {
    if (!atmsData) return [];
    const atmsList = Array.isArray(atmsData) ? atmsData : Object.values(atmsData);
    return atmsList.filter((atm: any) => atm.locationId === Number(locationId));
  }, [atmsData, locationId]);
  
  const trks = useMemo(() => {
    if (!trksData) return [];
    const trksList = Array.isArray(trksData) ? trksData : Object.values(trksData);
    return trksList.filter((trk: any) => trk.locationId === Number(locationId));
  }, [trksData, locationId]);

  // Memoize the isSameDay function
  const isSameDay = useCallback((date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }, []);

  // Memoize the filtered rejects
  const locationRejects = useMemo(() => {
    return allRejects.filter((reject: IReject) => {
      // First, check if the reject belongs to this location's assets
      const isAtmReject = reject.atmId && atms.some((atm: any) => atm.id === reject.atmId);
      const isTrkReject = reject.trkId && trks.some((trk: any) => trk.id === reject.trkId);
      const belongsToLocation = isAtmReject || isTrkReject;

      // If it doesn't belong to this location, filter it out
      if (!belongsToLocation) return false;

      // If dateFilter is provided, check if the reject is from the same day
      // if (dateFilter) {
      //   const rejectDate = new Date(reject.rejectDate);
      //   return isSameDay(rejectDate, dateFilter);
      // }

      // If no dateFilter, include all rejects for this location
      return true;
    });
  }, [allRejects, atms, trks, dateFilter, isSameDay]);

  // Memoize the columns configuration
  const rejectColumns = useMemo(() => [
    {
      accessorKey: 'type',
      header: 'Type',
      Cell: ({ row }: any) => {
        return row.original.atmId ? 'ATM' : 'TRK';
      },
    },
    {
      accessorKey: 'assetId',
      header: 'Asset ID',
      Cell: ({ row }: any) => {
        const asset = row.original.atmId 
          ? atms.find((atm: any) => atm.id === row.original.atmId)
          : trks.find((trk: any) => trk.id === row.original.trkId);
          const safeAsset = asset as any;
        return safeAsset?.assetId || 'N/A';
      },
    },
    {
      accessorKey: 'rejectDate',
      header: 'Reject Date',
      Cell: ({ row }: any) => {
        return formatDateHourMinuteSecond(row.original.rejectDate);
      },
    },
    {
      accessorKey: 'reportedTotalCash',
      header: 'Reported Total Cash ($)',
      Cell: ({ row }: any) => {
        return `$${row.original.reportedTotalCash.toLocaleString()}`;
      },
    },
    {
      accessorKey: 'actualCashCount',
      header: 'Actual Cash Count ($)',
      Cell: ({ row }: any) => {
        return `$${row.original.actualCashCount.toLocaleString()}`;
      },
    },
    {
      accessorKey: 'difference',
      header: 'Difference ($)',
      Cell: ({ row }: any) => {
        const diff = row.original.difference;
        const color = diff < 0 ? 'red' : diff > 0 ? 'green' : 'inherit';
        return (<span style={{ color }}>${diff.toLocaleString()}</span>);
      },
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
    },
  ], [atms, trks]);

  // Memoize the toolbar render function
  const renderTopToolbar = useCallback(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          p: '0.5rem',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          Asset Rejects
          {dateFilter && (
            <span style={{ fontSize: '0.9em', marginLeft: '8px', color: 'rgba(0, 0, 0, 0.6)' }}>
              ({dateFilter.toLocaleDateString()})
            </span>
          )}
        </Typography>
        <ReportRejectActionButton locationIdFilter={Number(locationId)} plusButtonStyle={true} />
      </Box>
    );
  }, [locationId, dateFilter]);

  return (

      <MaterialReactTable
        columns={rejectColumns}
        data={locationRejects}
        enableColumnFilters={true}
        enableColumnActions={false}
        enableSorting={true}
        enableTopToolbar={true}
        enableBottomToolbar={true}
        enablePagination={true}
        muiTableBodyRowProps={{ hover: true }}
        renderTopToolbarCustomActions={renderTopToolbar}
        initialState={{
          sorting: [{ id: 'rejectDate', desc: true }],
          density: 'compact',
        }}
      />

  );
};

export default LocationRejectsTable; 